import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { LLink } from "@/components/common";
import { Switch } from "@/components/ui/switch";
import { getConsent, saveConsent, loadAnalytics } from "@/lib/consent";

const EASE = [0.22, 1, 0.36, 1];

function ConsentRow({ title, text, checked, onChange, disabled, alwaysLabel, testId }) {
  return (
    <div className="flex items-start justify-between gap-3 py-3 border-t border-bone/10 first:border-t-0">
      <div className="min-w-0">
        <p className="text-xs font-medium text-bone">{title}</p>
        <p className="mt-0.5 text-[11px] leading-relaxed text-bone/50">{text}</p>
      </div>
      {disabled ? (
        <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.18em] text-gold pt-1">{alwaysLabel}</span>
      ) : (
        <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} className="shrink-0 data-[state=checked]:bg-terra data-[state=unchecked]:bg-bone/20" />
      )}
    </div>
  );
}

export function CookieBanner() {
  const { t } = useLang();
  const c = t.consent;
  const [open, setOpen] = useState(false);
  const [configure, setConfigure] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    loadAnalytics();
    const saved = getConsent();
    if (saved) {
      setAnalytics(saved.analytics);
      setMarketing(saved.marketing);
    } else {
      const id = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(id);
    }
  }, []);

  useEffect(() => {
    const onOpen = () => {
      const saved = getConsent();
      if (saved) { setAnalytics(saved.analytics); setMarketing(saved.marketing); }
      setConfigure(true);
      setOpen(true);
    };
    window.addEventListener("hcm:consent-open", onOpen);
    return () => window.removeEventListener("hcm:consent-open", onOpen);
  }, []);

  const decide = (choice) => {
    saveConsent(choice);
    setOpen(false);
    setConfigure(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          role="dialog"
          aria-label={c.title}
          data-testid="cookie-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="fixed z-50 inset-x-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] md:inset-x-auto md:right-6 md:bottom-6 md:w-[340px] bg-ink/95 text-bone backdrop-blur-xl border border-bone/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
        >
          <div className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
                <Cookie size={13} strokeWidth={1.5} /> {c.eyebrow}
              </div>
              {configure && getConsent() && (
                <button type="button" onClick={() => { setOpen(false); setConfigure(false); }} aria-label={c.close} data-testid="cookie-close" className="text-bone/50 hover:text-bone transition-colors">
                  <X size={15} strokeWidth={1.5} />
                </button>
              )}
            </div>
            <h2 className="mt-2 font-serif text-xl leading-tight text-bone">{c.title}</h2>

            {!configure ? (
              <>
                <p className="mt-2 text-xs leading-relaxed text-bone/60">
                  {c.text}{" "}
                  <LLink to="cookies" data-testid="cookie-policy-link" className="underline underline-offset-4 decoration-bone/30 hover:decoration-bone text-bone/85">{c.more}</LLink>
                </p>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button type="button" data-testid="cookie-accept-all" onClick={() => decide({ analytics: true, marketing: true })} className="bg-bone text-charcoal px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-white transition-colors">
                    {c.acceptAll}
                  </button>
                  <button type="button" data-testid="cookie-reject" onClick={() => decide({ analytics: false, marketing: false })} className="border border-bone/25 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-bone hover:border-bone/60 transition-colors">
                    {c.reject}
                  </button>
                  <button type="button" data-testid="cookie-configure" onClick={() => setConfigure(true)} className="col-span-2 pt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-bone/50 hover:text-bone transition-colors">
                    {c.configure}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mt-3">
                  <ConsentRow title={c.necessary.t} text={c.necessary.d} disabled alwaysLabel={c.always} />
                  <ConsentRow title={c.analytics.t} text={c.analytics.d} checked={analytics} onChange={setAnalytics} testId="cookie-toggle-analytics" />
                  <ConsentRow title={c.marketing.t} text={c.marketing.d} checked={marketing} onChange={setMarketing} testId="cookie-toggle-marketing" />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button type="button" data-testid="cookie-save" onClick={() => decide({ analytics, marketing })} className="bg-bone text-charcoal px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-white transition-colors">
                    {c.save}
                  </button>
                  <button type="button" data-testid="cookie-accept-all-settings" onClick={() => decide({ analytics: true, marketing: true })} className="border border-bone/25 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-bone hover:border-bone/60 transition-colors">
                    {c.acceptAll}
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
